/** @jsxImportSource theme-ui */
import React, { useState } from 'react';
import { Box, Flex, Heading, Text } from 'theme-ui';
import { Formik } from 'formik';
import {
    useGetRedirectDomainsQuery,
    usePostRedirectDomainMutation,
    useDeleteRedirectDomainMutation,
    useGetCompaniesQuery,
} from '@/store/apiService';
import { Container } from '@/components/styled';
import Button from '@/components/Button';
import { GrayFormikField } from '@/components/GrayFormikField';
import DropdownComboBox from '@/components/DropdownComboBox';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { toast } from 'react-toastify';
import { AllowedRedirectDomainResponse } from '@/types/types';

type View = 'list' | 'create';

const DOMAIN_MAX_LEN = 255;

const validateAdd = (values: { CompanyId: string; Domain: string }) => {
    const errors: { CompanyId?: string; Domain?: string } = {};
    if (!values.CompanyId) errors.CompanyId = 'Required';
    const domain = values.Domain.trim();
    if (!domain) {
        errors.Domain = 'Required';
    } else if (domain.length > DOMAIN_MAX_LEN) {
        errors.Domain = `Max ${DOMAIN_MAX_LEN} characters`;
    } else if (/\s/.test(domain)) {
        errors.Domain = 'A domain cannot contain spaces';
    }
    return errors;
};

const DomainRow: React.FC<{ domain: AllowedRedirectDomainResponse }> = ({ domain }) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteDomain, deleteState] = useDeleteRedirectDomainMutation();

    return (
        <Flex
            sx={{
                gap: 3,
                p: 3,
                mb: 2,
                borderRadius: 'sm',
                backgroundColor: 'gray.1',
                alignItems: 'center',
            }}
        >
            <Text sx={{ color: 'gray.5', fontSize: 0 }}>#{domain.Id}</Text>
            <Text sx={{ fontWeight: 'bold' }}>{domain.Domain}</Text>
            <Flex sx={{ flexDirection: 'column', ml: 'auto', alignItems: 'flex-end' }}>
                <Text sx={{ fontSize: 1 }}>{domain.CompanyName ?? '—'}</Text>
                <Text sx={{ color: 'gray.5', fontSize: 0 }}>{domain.CompanyId}</Text>
            </Flex>
            <Text sx={{ color: 'gray.6', fontSize: 0 }}>
                {domain.CreatedDate
                    ? new Date(domain.CreatedDate).toLocaleDateString('sv-SE')
                    : ''}
            </Text>
            <Button variant="danger" type="button" onClick={() => setConfirmDelete(true)}>
                Delete
            </Button>
            {confirmDelete && (
                <ConfirmationModal
                    hideOnClickOutside
                    loading={deleteState.isLoading}
                    headingText={`Delete "${domain.Domain}"?`}
                    content="This company will no longer be able to redirect to this domain after a payment."
                    onNo={() => setConfirmDelete(false)}
                    onYes={() => {
                        deleteDomain({ Id: domain.Id })
                            .unwrap()
                            .then(() => setConfirmDelete(false))
                            .catch((err) => {
                                setConfirmDelete(false);
                                toast.error(
                                    err?.data?.ResponseStatus?.Message ??
                                        'Failed to delete redirect domain',
                                    { autoClose: 5000 },
                                );
                            });
                    }}
                />
            )}
        </Flex>
    );
};

const CreateRedirectDomain: React.FC<{ onDone: () => void }> = ({ onDone }) => {
    const [companySearch, setCompanySearch] = useState('');
    const companies = useGetCompaniesQuery({ Take: 20, Search: companySearch });
    const [postDomain] = usePostRedirectDomainMutation();

    const companyItems = (companies.data?.Results ?? []).map((c) => ({
        value: c.Id as string,
        label: c.Name ?? (c.Id as string),
    }));

    return (
        <Container>
            <Flex sx={{ alignItems: 'center', gap: 2, mb: 3 }}>
                <Button variant="secondary" type="button" onClick={onDone}>
                    Back
                </Button>
                <Heading as="h2">Add redirect domain</Heading>
            </Flex>
            <Formik
                initialValues={{ CompanyId: '', Domain: '' }}
                validate={validateAdd}
                onSubmit={(values, { setSubmitting }) => {
                    postDomain({ CompanyId: values.CompanyId, Domain: values.Domain.trim() })
                        .unwrap()
                        .then(() => onDone())
                        .catch((err) => {
                            setSubmitting(false);
                            toast.error(
                                err?.data?.ResponseStatus?.Message ??
                                    'Failed to add redirect domain',
                                { autoClose: 5000 },
                            );
                        });
                }}
            >
                {(formProps) => {
                    const companyError = (formProps.errors as { CompanyId?: string }).CompanyId;
                    const companyTouched = (formProps.touched as { CompanyId?: boolean }).CompanyId;
                    return (
                        <Flex sx={{ flexDirection: 'column', gap: 3 }}>
                            <Box>
                                <GrayFormikField
                                    name="CompanyId"
                                    value={formProps.values.CompanyId}
                                    type="dropdownsearch"
                                    label="Company"
                                    placeholder="Search companies by name"
                                    required
                                    onSearchChange={(val: string) => setCompanySearch(val)}
                                    onSelect={(item?: { value: string; label: string } | null) => {
                                        formProps.setFieldValue('CompanyId', item?.value ?? '');
                                        formProps.setFieldTouched('CompanyId', true, false);
                                    }}
                                    items={companyItems}
                                    isLoading={companies.isFetching}
                                    total={companies.data?.Total}
                                />
                                {companyTouched && companyError ? (
                                    <Text color="danger">{companyError}</Text>
                                ) : null}
                            </Box>
                            <GrayFormikField
                                name="Domain"
                                label="Domain"
                                required
                                placeholder="shop.example.com"
                                autoComplete="off"
                            />
                            <Text sx={{ color: 'gray.6', fontSize: 0 }}>
                                Enter a bare host such as <code>shop.example.com</code>. A full URL is
                                accepted and reduced to its host. Subdomains of the entered host are
                                also allowed.
                            </Text>
                            <Flex sx={{ justifyContent: 'flex-end', gap: 2 }}>
                                <Button variant="secondary" type="button" onClick={onDone}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    isLoading={formProps.isSubmitting}
                                    disabled={formProps.isSubmitting}
                                    onClick={formProps.submitForm}
                                >
                                    Save
                                </Button>
                            </Flex>
                        </Flex>
                    );
                }}
            </Formik>
        </Container>
    );
};

const RedirectDomains: React.FC = () => {
    const [view, setView] = useState<View>('list');
    const [filterCompanyId, setFilterCompanyId] = useState<string>('');
    const [companySearch, setCompanySearch] = useState('');

    const companies = useGetCompaniesQuery({ Take: 20, Search: companySearch });
    const { data, isLoading, isFetching } = useGetRedirectDomainsQuery(
        filterCompanyId ? { CompanyId: filterCompanyId } : {},
    );

    const companyItems = (companies.data?.Results ?? []).map((c) => ({
        value: c.Id as string,
        label: c.Name ?? (c.Id as string),
    }));

    const domains = data?.Results ?? [];

    if (view === 'create') {
        return <CreateRedirectDomain onDone={() => setView('list')} />;
    }

    return (
        <Container>
            <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Heading as="h2">Redirect domains</Heading>
                <Button onClick={() => setView('create')}>Add domain</Button>
            </Flex>
            <Text sx={{ color: 'gray.6', mb: 3, display: 'block' }}>
                Per-company allowlist of hosts a company may redirect to after a payment, beyond the
                implicit BokaMera domains and the company&apos;s own website. Used by the Qvickly
                payment-confirmation guard.
            </Text>

            <Flex sx={{ alignItems: 'flex-end', gap: 2, mb: 3 }}>
                <Box sx={{ minWidth: '320px' }}>
                    <DropdownComboBox
                        label="Filter by company"
                        placeholder="Search companies by name"
                        items={companyItems}
                        isLoading={companies.isFetching}
                        total={companies.data?.Total}
                        onSearchChange={(text) => setCompanySearch(text)}
                        onSelect={(item) => setFilterCompanyId((item?.value as string) ?? '')}
                    />
                </Box>
                {filterCompanyId && (
                    <Button
                        variant="secondary"
                        type="button"
                        onClick={() => {
                            setFilterCompanyId('');
                            setCompanySearch('');
                        }}
                    >
                        Clear filter
                    </Button>
                )}
            </Flex>

            {(isLoading || isFetching) && <Text>Loading...</Text>}
            {!isLoading && !isFetching && domains.length === 0 && (
                <Text>No redirect domains found.</Text>
            )}
            {domains.map((domain) => (
                <DomainRow key={domain.Id} domain={domain} />
            ))}
        </Container>
    );
};

export default RedirectDomains;
